import { useMemo } from 'react';
import { VENUE_TIMEZONE } from '@/lib/timezone';
/**
 * Derives the real-time activity feed from bookings.
 *
 * Presentation-agnostic: returns plain data so both the Classic and Mission
 * Control dashboards can render it with their own styling. (`MCStreamEvent` is
 * a data shape, not a styled component.)
 */
const WINDOW_MS = 24 * 60 * 60 * 1000;
const MAX_EVENTS = 50;
export function useDashboardStreamEvents(bookings, currentTime) {
    return useMemo(() => {
        const events = [];
        const now = currentTime.getTime();
        bookings.forEach((b) => {
            const created = new Date(b.createdAt).getTime();
            const start = new Date(b.startTime).getTime();
            const end = new Date(b.endTime).getTime();
            const status = (b.bookingStatus || b.status || '').toUpperCase();
            const payStatus = (b.paymentStatus || '').toUpperCase();
            const isQuickSale = b.bookingSource === 'QUICK_SALE';
            const roomName = b.roomName || 'Unknown Room';
            if (now - created < WINDOW_MS) {
                events.push({
                    id: `create-${b.id}`,
                    timestamp: new Date(b.createdAt),
                    type: isQuickSale ? 'QuickSale' : 'BookingCreate',
                    primary: isQuickSale
                        ? `$${b.price.toFixed(2)} · ${payStatus || 'UNPAID'}`
                        : `${roomName} · ${b.players}p · ${b.duration}h`,
                    secondary: b.customerName,
                    meta: b.bookingSource || 'ONLINE',
                    linkHref: `/pos/booking/${b.id}`,
                });
            }
            if (!isQuickSale && status === 'BOOKED' && start <= now && end > now && now - start < WINDOW_MS) {
                events.push({
                    id: `start-${b.id}`,
                    timestamp: new Date(start),
                    type: 'SessionStart',
                    primary: `${roomName} · until ${new Date(b.endTime).toLocaleTimeString('en-CA', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                        timeZone: VENUE_TIMEZONE,
                    })}`,
                    secondary: b.customerName,
                    linkHref: `/pos/booking/${b.id}`,
                });
            }
            if (!isQuickSale && status === 'COMPLETED' && now - end < WINDOW_MS) {
                events.push({
                    id: `end-${b.id}`,
                    timestamp: new Date(end),
                    type: 'SessionEnd',
                    primary: `${roomName} · $${b.price.toFixed(2)}`,
                    secondary: b.customerName,
                    linkHref: `/pos/booking/${b.id}`,
                });
            }
            if (payStatus === 'PAID' && b.updatedAt && now - new Date(b.updatedAt).getTime() < WINDOW_MS) {
                events.push({
                    id: `pay-${b.id}`,
                    timestamp: new Date(b.updatedAt),
                    type: 'PaymentSettle',
                    primary: isQuickSale
                        ? `Quick Sale · $${b.price.toFixed(2)}`
                        : `${roomName} · $${b.price.toFixed(2)}`,
                    secondary: b.customerName,
                    linkHref: `/pos/booking/${b.id}`,
                });
            }
        });
        return events
            .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
            .slice(0, MAX_EVENTS);
    }, [bookings, currentTime]);
}
export default useDashboardStreamEvents;
